import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Music, Film, Play } from "lucide-react";
import { api, artUrl, thumbUrl } from "@/lib/api";
import MediaModal from "@/components/home/MediaModal";

const SHOTS = [
  {
    src: "/gallery/spiritual-assassin-the-ghost.jpg",
    caption: "Spiritual Assassin — The Ghost",
    tag: "New Single · Coming Soon",
  },
  {
    src: "/gallery/control-room.jpg",
    caption: "The Control Room",
    tag: "Live Production",
  },
  {
    src: "/gallery/ayiti-flag.jpg",
    caption: "Flag Up",
    tag: "Haiti · Pride",
  },
  {
    src: "/gallery/creator-founder.jpg",
    caption: "Creator & Founder",
    tag: "Simplicity Films, LLC",
  },
  {
    src: "/gallery/heritage.jpg",
    caption: "Heritage",
    tag: "Roots · Legacy",
  },
  {
    src: "/gallery/behind-the-lens.jpg",
    caption: "Behind the Lens",
    tag: "On Location · All Access",
  },
  {
    src: "/gallery/park-savior.jpg",
    caption: "Park Savior",
    tag: "On Location · EP 006",
  },
];

export default function Gallery() {
  const [lightbox, setLightbox] = useState(null);
  const [featured, setFeatured] = useState([]);
  const [artworks, setArtworks] = useState([]);
  const [mediaActive, setMediaActive] = useState(null);

  useEffect(() => {
    api.get("/media").then((r) => setFeatured(r.data.filter((m) => m.featured))).catch(() => {});
    api.get("/artworks").then((r) => setArtworks(r.data)).catch(() => {});
  }, []);

  return (
    <section id="gallery" data-testid="gallery-section" className="relative py-20 lg:py-32 px-4 sm:px-8 lg:px-16">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-14"
        >
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-blood mb-4">Spotlight</p>
          <h2 className="font-display font-black uppercase text-3xl sm:text-4xl lg:text-5xl tracking-tight">
            Frames from the field
          </h2>
        </motion.div>

        {featured.length > 0 && (
          <div className="mb-10" data-testid="gallery-featured-media">
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-blue-400 mb-4">Featured Drops</p>
            <div className="flex gap-4 overflow-x-auto pb-2">
              {featured.map((m, i) => (
                <button
                  key={m.id}
                  data-testid={`gallery-featured-${i}`}
                  onClick={() => setMediaActive(m)}
                  className="group shrink-0 w-72 sm:w-80 bg-elevate border border-white/10 hover:border-blood/60 text-left clip-frame transition-colors duration-300 overflow-hidden"
                >
                  {m.thumbnail_path ? (
                    <span className="relative block aspect-video bg-black overflow-hidden" data-testid={`gallery-featured-thumb-${i}`}>
                      <img src={thumbUrl(m.thumbnail_path)} alt={m.title} loading="lazy"
                        className="w-full h-full object-cover opacity-85 group-hover:opacity-100 group-hover:scale-105 transition-all duration-500" />
                      <span className="absolute inset-0 flex items-center justify-center">
                        <span className="w-12 h-12 flex items-center justify-center bg-blood/90 text-white group-hover:scale-110 transition-transform duration-300">
                          <Play size={18} fill="currentColor" />
                        </span>
                      </span>
                    </span>
                  ) : null}
                  <span className="block p-5">
                    <span className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-blue-400 mb-3">
                      {m.kind === "video" ? <Film size={12} /> : <Music size={12} />} {m.kind}
                    </span>
                    {!m.thumbnail_path && (
                      <span className="w-10 h-10 flex items-center justify-center bg-blood/15 text-blood group-hover:bg-blood group-hover:text-white transition-colors duration-300 mb-3">
                        <Play size={16} fill="currentColor" />
                      </span>
                    )}
                    <span className="block font-display font-bold uppercase text-lg leading-tight">{m.title}</span>
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {SHOTS.map((shot, i) => (
            <motion.button
              key={shot.caption}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6, delay: 0.05 * i, ease: [0.16, 1, 0.3, 1] }}
              onClick={() => setLightbox(shot)}
              data-testid={`gallery-item-${i}`}
              className={`group relative overflow-hidden clip-frame border border-white/10 hover:border-blood/60 transition-colors duration-500 ${
                i === 0 || i === 3 ? "lg:row-span-2 lg:h-full h-72" : "h-72"
              }`}
            >
              <img
                src={shot.src}
                alt={shot.caption}
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover grayscale-[35%] group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-transparent to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-5 text-left">
                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-blood">{shot.tag}</p>
                <p className="font-display font-bold uppercase text-xl tracking-wide">{shot.caption}</p>
              </div>
            </motion.button>
          ))}
          {artworks.map((a, i) => (
            <motion.button
              key={a.id}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
              onClick={() => setLightbox({ src: artUrl(a.storage_path), caption: a.prompt })}
              data-testid={`gallery-artwork-${i}`}
              className="group relative overflow-hidden clip-frame border border-gold/30 hover:border-gold/70 transition-colors duration-500 h-72"
            >
              <img
                src={artUrl(a.storage_path)}
                alt={a.prompt}
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-all duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-transparent to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-5 text-left">
                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold">AI Cover Art</p>
                <p className="font-display font-bold uppercase text-xl tracking-wide line-clamp-1">{a.prompt}</p>
              </div>
            </motion.button>
          ))}
        </div>
      </div>

      <MediaModal item={mediaActive} onClose={() => setMediaActive(null)} />

      <AnimatePresence>
        {lightbox && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4 sm:p-10"
            onClick={() => setLightbox(null)}
            data-testid="gallery-lightbox"
          >
            <motion.figure
              initial={{ scale: 0.94, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.94, opacity: 0 }}
              transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
              className="relative max-w-4xl w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <img src={lightbox.src} alt={lightbox.caption} className="w-full max-h-[75vh] object-contain" />
              <figcaption className="mt-4 flex items-center justify-between">
                <span className="font-display font-bold uppercase text-xl">{lightbox.caption}</span>
                <button
                  data-testid="gallery-lightbox-close-button"
                  onClick={() => setLightbox(null)}
                  className="text-gray-400 hover:text-blood transition-colors"
                  aria-label="Close"
                >
                  <X size={24} />
                </button>
              </figcaption>
            </motion.figure>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
